import { useState, useRef, useMemo } from 'react'
import { motion } from 'framer-motion'
import { EntityCard, FloatingActionButton, GlassPanel, Icon, LimitUpgradePrompt } from '../components/common'
import { EntityModal } from '../components/EntityModal'
import { useEvents, useEventOperations } from '../hooks/useFirestore'
import { useProject } from '../context/ProjectContext'
import { useLimits } from '../hooks/useLimits'
import { deleteFiles, extractPublicIdFromUrl } from '../services/storage'
import { updateBidirectionalLinks, removeBidirectionalLinksOnDelete } from '../services/bidirectionalLinks'
import { COLLECTIONS } from '../services/firestore'
import './EventsView.css'

// LocalStorage key for tier filter persistence
const TIER_FILTER_KEY = 'tierFilters_events'

/**
 * Helper to get image URL from either string or object format
 * Supports both legacy (string) and new ({ url, publicId }) formats
 */
const getImageUrl = (image) => {
  if (!image) return ''
  if (typeof image === 'string') return image
  return image.url || ''
}

/**
 * Helper to collect all image publicIds from an entity
 * Returns array of publicIds for deletion
 */
const collectImagePublicIds = (entity) => {
  const publicIds = []
  
  // Primary image
  if (entity.primaryImage) {
    if (typeof entity.primaryImage === 'object' && entity.primaryImage.publicId) {
      publicIds.push(entity.primaryImage.publicId)
    } else if (typeof entity.primaryImage === 'string') {
      // Try to extract from URL for legacy data
      const extracted = extractPublicIdFromUrl(entity.primaryImage)
      if (extracted) publicIds.push(extracted)
    }
  }
  
  // Extra images
  if (entity.extraImages && Array.isArray(entity.extraImages)) {
    for (const img of entity.extraImages) {
      if (typeof img === 'object' && img.publicId) {
        publicIds.push(img.publicId)
      } else if (typeof img === 'string') {
        const extracted = extractPublicIdFromUrl(img)
        if (extracted) publicIds.push(extracted)
      } else if (typeof img === 'object' && img.url) {
        const extracted = extractPublicIdFromUrl(img.url)
        if (extracted) publicIds.push(extracted)
      }
    }
  }
  
  return publicIds.filter(Boolean)
}

// Page transition variants
const pageVariants = {
  initial: { 
    opacity: 0, 
    y: 20,
    filter: 'blur(4px)'
  },
  animate: { 
    opacity: 1, 
    y: 0,
    filter: 'blur(0px)',
    transition: {
      duration: 0.3,
      ease: [0.4, 0, 0.2, 1]
    }
  },
  exit: { 
    opacity: 0, 
    y: -20,
    filter: 'blur(4px)',
    transition: {
      duration: 0.2
    }
  }
}

// Container variants for staggered children
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.08,
      delayChildren: 0.1,
    },
  },
}

/**
 * EventsView - Grid display of event cards with CRUD operations
 */
export default function EventsView() {
  // Modal state
  const [modalOpen, setModalOpen] = useState(false)
  const [modalMode, setModalMode] = useState('create')
  const [selectedEvent, setSelectedEvent] = useState(null)
  const [originRect, setOriginRect] = useState(null)
  const [limitPromptOpen, setLimitPromptOpen] = useState(false)
  const fabRef = useRef(null)

  // Tier filter state (default: A and B shown, C hidden)
  const [tierFilters, setTierFilters] = useState(() => {
    try {
      const saved = localStorage.getItem(TIER_FILTER_KEY)
      return saved ? JSON.parse(saved) : { A: true, B: true, C: false }
    } catch {
      return { A: true, B: true, C: false }
    }
  })

  // Project context for scoped data access and permissions
  const { projectId, can } = useProject()
  const canCreate = can('create')
  
  // Limits checking
  const { canCreate: canCreateEntity, getLimit } = useLimits(projectId)

  // Firestore data and operations (project-scoped)
  const { data: events, loading, error } = useEvents(projectId, {
    orderByField: 'date',
    orderDirection: 'asc',
  })
  const eventOps = useEventOperations(projectId)

  // Filter events by tier
  const filteredEvents = useMemo(() => {
    if (!events) return []
    return events.filter(event => {
      const tier = (event.tier || 'B').toUpperCase()
      return tierFilters[tier] !== false
    })
  }, [events, tierFilters])

  // Toggle a tier filter
  const toggleTier = (tier) => {
    const newFilters = { ...tierFilters, [tier]: !tierFilters[tier] }
    setTierFilters(newFilters)
    try {
      localStorage.setItem(TIER_FILTER_KEY, JSON.stringify(newFilters))
    } catch {
      // Ignore localStorage errors
    }
  }

  // Handle create click - check limits first
  const handleCreateClick = (clickEvent) => {
    if (!canCreateEntity('events')) {
      setLimitPromptOpen(true)
      return
    }
    openCreateModal(clickEvent)
  }

  // Open modal in create mode
  const openCreateModal = (event) => {
    setModalMode('create')
    setSelectedEvent(null)
    if (event?.currentTarget) {
      setOriginRect(event.currentTarget.getBoundingClientRect())
    } else if (fabRef.current) {
      setOriginRect(fabRef.current.getBoundingClientRect())
    }
    setModalOpen(true)
  }

  // Open modal in edit mode
  const openEditModal = (eventData, clickEvent) => {
    setModalMode('edit')
    setSelectedEvent(eventData)
    if (clickEvent?.currentTarget) {
      setOriginRect(clickEvent.currentTarget.getBoundingClientRect())
    }
    setModalOpen(true)
  }

  // Handle save (create or update)
  const handleSave = async (data) => {
    try {
      if (modalMode === 'create') {
        const newId = await eventOps.add(data)
        // Update bidirectional links for the new entity
        await updateBidirectionalLinks(projectId, COLLECTIONS.EVENTS, newId, null, data)
      } else {
        await eventOps.update(selectedEvent.id, data)
        // Update bidirectional links (compare old vs new links)
        await updateBidirectionalLinks(projectId, COLLECTIONS.EVENTS, selectedEvent.id, selectedEvent, data)
      }
      setModalOpen(false)
    } catch (err) {
      console.error('Failed to save event:', err)
      throw err
    }
  }

  // Handle delete (also deletes associated Cloudinary images and cleans up bidirectional links)
  const handleDelete = async () => {
    try {
      // Collect all image publicIds from the event
      const publicIds = collectImagePublicIds(selectedEvent)
      
      // Delete images from Cloudinary (don't fail if this fails)
      if (publicIds.length > 0) {
        console.log('Deleting Cloudinary images:', publicIds)
        const deleteResult = await deleteFiles(publicIds)
        if (deleteResult.errors?.length > 0) {
          console.warn('Some images failed to delete:', deleteResult.errors)
        }
      }
      
      // Clean up bidirectional links before deleting
      await removeBidirectionalLinksOnDelete(projectId, COLLECTIONS.EVENTS, selectedEvent.id, selectedEvent)
      
      // Delete the event document from Firestore
      await eventOps.remove(selectedEvent.id)
      setModalOpen(false)
    } catch (err) {
      console.error('Failed to delete event:', err)
      throw err
    }
  }

  // Build card details from event data
  const getCardDetails = (event) => {
    const details = []
    
    if (event.date) {
      details.push({ 
        label: 'Date', 
        value: event.date.slice(0, 15) + (event.date.length > 15 ? '...' : '')
      })
    }
    
    if (event.era) {
      details.push({ 
        label: 'Era', 
        value: event.era.slice(0, 15) + (event.era.length > 15 ? '...' : '')
      })
    }
    
    // Show enabled/disabled status
    const isEnabled = event.enabled !== false
    details.push({ 
      label: 'Status', 
      value: isEnabled ? 'Enabled' : 'Disabled'
    })
    
    if (event.linkedLocations?.length > 0) {
      details.push({ 
        label: 'Locations', 
        value: event.linkedLocations.length.toString()
      })
    } else if (event.linkedCharacters?.length > 0) {
      details.push({ 
        label: 'Characters', 
        value: event.linkedCharacters.length.toString()
      })
    }
    
    return details.slice(0, 4)
  }

  // Get subtitle from description
  const getSubtitle = (event) => {
    if (!event.description) return 'No description'
    return event.description.slice(0, 50) + (event.description.length > 50 ? '...' : '')
  }

  return (
    <motion.div 
      className="events-view"
      variants={pageVariants}
      initial="initial"
      animate="animate"
      exit="exit"
    >
      {/* Page Header */}
      <header className="events-view__header">
        <div className="events-view__title-group">
          <span className="events-view__icon"><Icon name="events" size={28} /></span>
          <h1 className="events-view__title">Events</h1>
        </div>
        <p className="events-view__subtitle">
          {loading ? 'Loading...' : `${filteredEvents.length} event${filteredEvents.length !== 1 ? 's' : ''}`}
          {!loading && events.length !== filteredEvents.length && ` (${events.length} total)`}
        </p>
      </header>

      {/* Tier Filter Bar */}
      {!loading && !error && events.length > 0 && (
        <div className="events-view__filters">
          <div className="events-view__tier-filters">
            <span className="events-view__filter-label">Tier:</span>
            {['A', 'B', 'C'].map(tier => (
              <button
                key={tier}
                className={`events-view__tier-btn events-view__tier-btn--${tier.toLowerCase()} ${tierFilters[tier] ? 'events-view__tier-btn--active' : ''}`}
                onClick={() => toggleTier(tier)}
                title={`${tierFilters[tier] ? 'Hide' : 'Show'} Tier ${tier} events`}
              >
                {tier}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Error State */}
      {error && (
        <GlassPanel glow="amber" padding="lg" className="events-view__error">
          <h3>Error loading events</h3>
          <p>{error.message}</p>
        </GlassPanel>
      )}

      {/* Loading State */}
      {loading && !error && (
        <div className="events-view__loading">
          <div className="events-view__spinner" />
          <p>Loading events...</p>
        </div>
      )}

      {/* Empty State */}
      {!loading && !error && events.length === 0 && (
        <GlassPanel glow="cyan" padding="xl" className="events-view__empty">
          <div className="events-view__empty-icon"><Icon name="events" size={48} /></div>
          <h2>No events yet</h2>
          <p>{canCreate ? 'Create your first event to start building your timeline.' : 'No events have been added to this project yet.'}</p>
        </GlassPanel>
      )}

      {/* No Results State (when filters hide all) */}
      {!loading && !error && events.length > 0 && filteredEvents.length === 0 && (
        <div className="events-view__no-results">
          <div className="events-view__no-results-icon"><Icon name="search" size={48} /></div>
          <h3>No matching events</h3>
          <p>Try adjusting your tier filters to see more events.</p>
        </div>
      )}

      {/* Event Grid */}
      {!loading && !error && filteredEvents.length > 0 && (
        <motion.div 
          className="events-view__grid"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          {filteredEvents.map((event, index) => (
            <EntityCard
              key={event.id}
              type="event"
              name={event.title}
              image={getImageUrl(event.primaryImage)}
              subtitle={getSubtitle(event)}
              details={getCardDetails(event)}
              tier={event.tier}
              index={index}
              onClick={(e) => openEditModal(event, e)}
            />
          ))}
        </motion.div>
      )}

      {/* Floating Action Button - only shown if user can create */}
      {canCreate && (
        <div ref={fabRef} data-onboarding="add-entity">
          <FloatingActionButton
            icon={<Icon name="plus" size={24} />}
            variant="primary"
            size="lg"
            position="bottom-right"
            label="Add new event"
            onClick={handleCreateClick}
          />
        </div>
      )}

      {/* Entity Modal */}
      <EntityModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        entityType="event"
        mode={modalMode}
        initialData={selectedEvent}
        onSave={handleSave}
        onDelete={modalMode === 'edit' ? handleDelete : undefined}
        originRect={originRect}
      />

      {/* Limit Upgrade Prompt */}
      <LimitUpgradePrompt
        isOpen={limitPromptOpen}
        onClose={() => setLimitPromptOpen(false)}
        entityType="events"
        limit={getLimit('events')}
      />
    </motion.div>
  )
}

